import Divider from "@mui/material/Divider";
import ListItemIcon from "@mui/material/ListItemIcon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import React from "react";

import { menuItemSx, menuPaperSx } from "../lib/menu-chrome";

export type ContextMenuEntry =
  | { kind: "divider" }
  | { kind: "caption"; label: string; detail?: string }
  | {
      kind: "item";
      label: React.ReactNode;
      icon?: React.ReactNode;
      onClick: () => void;
      danger?: boolean;
      disabled?: boolean;
    };

export interface ContextMenuPosition {
  mouseX: number;
  mouseY: number;
}

/**
 * A right-click menu anchored to the cursor. Every entry is data, so the grid
 * can build a different list for a cell, a row and a column header without
 * three copies of the markup.
 */
export const ContextMenu: React.FC<{
  position: ContextMenuPosition | null;
  entries: ContextMenuEntry[];
  onClose: () => void;
}> = ({ position, entries, onClose }) => (
  <Menu
    open={!!position}
    onClose={onClose}
    // No exit fade: Edit opens a drawer at once, and the fading invisible
    // backdrop swallowed the first click in it.
    transitionDuration={{ exit: 0 }}
    anchorReference="anchorPosition"
    anchorPosition={
      position ? { top: position.mouseY, left: position.mouseX } : undefined
    }
    // A right-click inside the menu should dismiss it rather than stack a
    // second browser menu on top.
    slotProps={{
      root: {
        onContextMenu: (event: React.MouseEvent) => {
          event.preventDefault();
          onClose();
        },
      },
      paper: {
        sx: { ...menuPaperSx, minWidth: 232 },
      },
    }}
    MenuListProps={{ dense: true, sx: { py: 0 } }}
  >
    {entries.map((entry, index) => {
      if (entry.kind === "divider") {
        return (
          <Divider
            key={`divider-${index}`}
            sx={{ my: 0.5, borderColor: "var(--od-border)" }}
          />
        );
      }

      if (entry.kind === "caption") {
        return (
          <li
            key={`caption-${index}`}
            className="px-3 pt-1.5 pb-1 text-xs font-semibold uppercase tracking-wider text-od-muted"
          >
            <div className="truncate">{entry.label}</div>
            {entry.detail && (
              <div className="truncate font-mono text-xs font-normal normal-case tracking-normal text-od-text/80">
                {entry.detail}
              </div>
            )}
          </li>
        );
      }

      return (
        <MenuItem
          key={`item-${index}`}
          disabled={entry.disabled}
          onClick={() => {
            onClose();
            entry.onClick();
          }}
          sx={{
            ...menuItemSx,
            py: 0.6,
            ...(entry.danger ? { color: "var(--od-danger)" } : {}),
          }}
        >
          {entry.icon && (
            <ListItemIcon
              sx={{
                minWidth: "28px !important",
                color: entry.danger ? "var(--od-danger)" : "var(--od-muted)",
                "& .MuiSvgIcon-root": { fontSize: 17 },
              }}
            >
              {entry.icon}
            </ListItemIcon>
          )}
          <span className="truncate">{entry.label}</span>
        </MenuItem>
      );
    })}
  </Menu>
);

export default ContextMenu;
